import React, { useState, useEffect } from 'react';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Chip,
  Grid
} from '@mui/material';
import { Assignment as AssignmentIcon } from '@mui/icons-material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { get } from '../../services/apiService';

const SnykTeamTargetTable = ({ showUnresolved = true, outOfSLA = false, collection = '' }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dueDate, setDueDate] = useState(null);

  useEffect(() => {
    fetchData();
  }, [dueDate, showUnresolved, outOfSLA, collection]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const queryParams = new URLSearchParams();
      if (outOfSLA) {
        queryParams.append('outOfSLA', 'true');
      } else {
        if (dueDate) {
          const dateStr = dayjs(dueDate).format('YYYY-MM-DD');
          queryParams.append('dueDate', dateStr);
        }
        if (showUnresolved) {
          queryParams.append('showUnresolved', 'true');
        }
      }
      if (collection) {
        queryParams.append('projectCollection', collection);
      }
      const queryString = queryParams.toString();
      const url = `/api/snyk/team-target-summary${queryString ? `?${queryString}` : ''}`;
      const response = await get(url);
      
      if (response && response.success && response.data) {
        // Data is now grouped by stream: [{ stream, teams: [...] }]
        setData(response.data);
      } else {
        throw new Error('Invalid response format');
      }
    } catch (err) {
      console.error('Error fetching team target summary:', err);
      setError(err.message || 'Failed to fetch team target data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
          <CircularProgress />
        </Box>
      </Paper>
    );
  }

  if (error) {
    return (
      <Paper sx={{ p: 3, mb: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Paper>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          <AssignmentIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Critical, High & Medium Issues by Team (Total: 0)
        </Typography>
        <Typography variant="body2" color="text.secondary">
          No data available
        </Typography>
      </Paper>
    );
  }

  // Filter out empty streams and prepare stream data
  const validStreams = data.filter(streamGroup => 
    streamGroup.teams && streamGroup.teams.length > 0
  );

  // Calculate total sum of all "Total" values across all streams and teams
  const totalCount = validStreams.reduce((sum, streamGroup) => {
    const streamTotal = (streamGroup.teams || []).reduce((teamSum, team) => {
      return teamSum + (team.total || 0);
    }, 0);
    return sum + streamTotal;
  }, 0);

  if (validStreams.length === 0) {
    return (
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          <AssignmentIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Critical, High & Medium Issues by Team (Total: 0)
        </Typography>
        <Typography variant="body2" color="text.secondary">
          No team data available
        </Typography>
      </Paper>
    );
  }

  // Distribute streams across 3 tables (one stream per table, no mixing)
  // First 3 streams each get their own table
  // If more than 3 streams, remaining streams go into the 3rd table
  const tableChunks = [];
  
  // First table: first stream (if exists)
  tableChunks.push(validStreams.length > 0 ? [validStreams[0]] : []);
  
  // Second table: second stream (if exists)
  tableChunks.push(validStreams.length > 1 ? [validStreams[1]] : []);
  
  // Third table: third stream and any remaining streams (if exist)
  if (validStreams.length > 2) {
    tableChunks.push(validStreams.slice(2));
  } else {
    tableChunks.push([]);
  }

  // Render a table for one or more streams (one table per stream ideally)
  const renderTable = (streamsData, tableIndex) => {
    if (streamsData.length === 0) {
      return (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell><strong>Team</strong></TableCell>
                <TableCell align="right"><strong>Critical</strong></TableCell>
                <TableCell align="right"><strong>High</strong></TableCell>
                <TableCell align="right"><strong>Medium</strong></TableCell>
                <TableCell align="right"><strong>Total</strong></TableCell>
                <TableCell align="right"><strong>Common Library</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography variant="body2" color="text.secondary">
                    No data
                  </Typography>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      );
    }

    return (
      <TableContainer component={Paper} variant="outlined">
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell><strong>Team</strong></TableCell>
              <TableCell align="right"><strong>Critical</strong></TableCell>
              <TableCell align="right"><strong>High</strong></TableCell>
              <TableCell align="right"><strong>Total</strong></TableCell>
              <TableCell align="right"><strong>Common Library</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {streamsData.map((streamGroup, streamIndex) => {
              const streamName = streamGroup.stream || 'Unassigned Stream';
              const teams = streamGroup.teams || [];
              
              return (
                <React.Fragment key={`stream-${streamIndex}`}>
                  {/* Stream header row */}
                  <TableRow sx={{ backgroundColor: 'rgba(25, 118, 210, 0.08)' }}>
                    <TableCell colSpan={6} sx={{ fontWeight: 600, fontSize: '0.875rem' }}>
                      <Chip
                        label={streamName}
                        color="primary"
                        size="small"
                        sx={{ fontWeight: 600 }}
                      />
                    </TableCell>
                  </TableRow>
                  {/* Team rows for this stream */}
                  {teams.map((row, teamIndex) => (
                    <TableRow key={`${tableIndex}-${streamIndex}-${teamIndex}`} hover>
                      <TableCell sx={{ fontSize: '0.875rem', pl: 3 }}>
                        {row.teamTarget || 'Unassigned'}
                      </TableCell>
                      <TableCell align="right">
                        <Chip
                          label={row.critical || 0}
                          size="small"
                          color="error"
                          variant={row.critical > 0 ? 'filled' : 'outlined'}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Chip
                          label={row.high || 0}
                          size="small"
                          color="warning"
                          variant={row.high > 0 ? 'filled' : 'outlined'}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Chip
                          label={row.medium || 0}
                          size="small"
                          color="info"
                          variant={row.medium > 0 ? 'filled' : 'outlined'}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <strong>{row.total || 0}</strong>
                      </TableCell>
                      <TableCell align="right">
                        {row.commonLibraryIssuesCount || 0}
                      </TableCell>
                    </TableRow>
                  ))}
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={2}>
        <Typography variant="h6" gutterBottom>
          <AssignmentIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Critical, High & Medium Issues by Team (Total: {totalCount})
        </Typography>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            label="Filter by Due Date"
            value={dueDate}
            onChange={(newValue) => setDueDate(newValue)}
            slotProps={{
              textField: {
                size: 'small',
                sx: { minWidth: 200 }
              },
              actionBar: {
                actions: ['clear', 'today']
              }
            }}
          />
        </LocalizationProvider>
      </Box>
      <Grid container spacing={2}>
        {tableChunks.map((streamsData, index) => (
          <Grid item xs={12} md={4} key={index}>
            {renderTable(streamsData, index)}
          </Grid>
        ))}
      </Grid>
    </Paper>
  );
};

export default SnykTeamTargetTable;

